import { SEOConfig, MetaTag, SEOOptions, StructuredData } from './types';
import { unref } from 'vue';

export class SEOManager {
    private config: SEOConfig;
    private managedElements: Set<Element> = new Set();
    private store: any;
    private isNativeApp = false;

    constructor(config: SEOConfig = {}, store?: any) {
        this.store = store;

        // Check if running on native platform
        if (this.store && this.store.getters.isNative) {
            this.isNativeApp = true;
            console.log('SEOManager: Running on native platform, SEO operations will be skipped');
        }

        this.config = {
            defaultTitle: 'Donatos Pizza',
            defaultDescription: '',
            defaultOgImage: '',
            defaultKeywords: [],
            fallbackLanguage: 'en',
            host: '',
            autoRobots: true,
            titlePrefix: 'Donatos Pizza — ',
            fallbackKeywords: 'Pizza, Subs, Salads, Wings, Dine in, Carryout, Delivery, Catering, Thin crust, Cauliflower crust, Gluten Free crust',
            ...config,
        };

        // Set initial defaults only if not on native platform
        if (!this.isNativeApp) {
            this.setDefaults();
        }
    }

    getConfig(): SEOConfig {
        return this.config;
    }

    private setDefaults(): void {
        if (this.config.defaultTitle) {
            this.setTitle(this.config.defaultTitle, true);
        }
        if (this.config.defaultDescription) {
            this.setMetaTag('description', this.config.defaultDescription);
        }
        if (this.config.defaultOgImage) {
            this.setMetaTag('og:image', this.config.defaultOgImage, 'property');
        }
        if (this.config.defaultKeywords && this.config.defaultKeywords.length > 0) {
            this.setMetaTag('keywords', this.config.defaultKeywords.join(', '));
        }

        // Set environment-based robots meta tag
        if (this.config.autoRobots) {
            this.setEnvironmentRobots();
        }

        // Set current page URL in og:url
        this.setCurrentPageUrl();
    }

    setTitle(title: string | null | undefined, skipTemplate = false): void {
        const finalTitle = title || this.config.defaultTitle || '';

        if (finalTitle === this.config.defaultTitle || skipTemplate) {
            document.title = finalTitle;
        } else if (this.config.titleTemplate) {
            if (typeof this.config.titleTemplate === 'function') {
                document.title = this.config.titleTemplate(finalTitle);
            } else {
                document.title = this.config.titleTemplate.replace('%s', finalTitle);
            }
        } else {
            // Apply title prefix if configured
            if (this.config.titlePrefix && finalTitle && !finalTitle.startsWith(this.config.titlePrefix)) {
                document.title = this.config.titlePrefix + finalTitle;
            } else {
                document.title = finalTitle;
            }
        }
    }

    setMetaTag(key: string, content: string, attributeType: 'name' | 'property' | 'httpEquiv' = 'name'): void {
        // Handle keywords fallback
        if (key === 'keywords' && !content && this.config.fallbackKeywords) {
            content = this.config.fallbackKeywords;
        }

        // Handle image URL processing
        if ((key === 'og:image' || key === 'twitter:image') && content) {
            content = this.processImageUrl(content);
        }

        if (!content) {
            // Remove the tag if content is empty
            this.removeMetaTag(key, attributeType);
            return;
        }

        const selector = `meta[${attributeType}="${key}"]`;
        let meta = document.querySelector(selector) as HTMLMetaElement;

        if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute(attributeType, key);
            meta.setAttribute('data-seo-managed', 'true');
            document.head.appendChild(meta);
            this.managedElements.add(meta);
        }

        meta.content = content;
    }

    private removeMetaTag(key: string, attributeType: 'name' | 'property' | 'httpEquiv' = 'name'): void {
        const selector = `meta[${attributeType}="${key}"]`;
        const meta = document.querySelector(selector);
        if (meta) {
            this.managedElements.delete(meta);
            meta.remove();
        }
    }

    setMultipleMetaTags(tags: MetaTag[]): void {
        tags.forEach((tag) => {
            if (tag.name) {
                this.setMetaTag(tag.name, tag.content, 'name');
            } else if (tag.property) {
                this.setMetaTag(tag.property, tag.content, 'property');
            } else if (tag.httpEquiv) {
                this.setMetaTag(tag.httpEquiv, tag.content, 'httpEquiv');
            }
        });
    }

    setCanonical(url: string): void {
        if (!url) {
            this.removeCanonical();
            return;
        }

        let canonicalUrl = url;

        // Make URL absolute if it's relative
        if (this.config.host && !url.startsWith('http')) {
            canonicalUrl = this.config.host + (url.startsWith('/') ? '' : '/') + url;
        }

        let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;

        if (!link) {
            link = document.createElement('link');
            link.rel = 'canonical';
            link.setAttribute('data-seo-managed', 'true');
            document.head.appendChild(link);
            this.managedElements.add(link);
        }

        link.href = canonicalUrl;
    }

    private removeCanonical(): void {
        const link = document.querySelector('link[rel="canonical"]');
        if (link) {
            this.managedElements.delete(link);
            link.remove();
        }
    }

    setStructuredData(data: StructuredData | null, id: string): void {
        if (!data) {
            this.removeStructuredData(id);
            return;
        }

        const selector = `script[type="application/ld+json"][data-seo-id="${id}"]`;
        let script = document.querySelector(selector) as HTMLScriptElement;

        if (!script) {
            script = document.createElement('script');
            script.type = 'application/ld+json';
            script.setAttribute('data-seo-id', id);
            script.setAttribute('data-seo-managed', 'true');
            document.head.appendChild(script);
            this.managedElements.add(script);
        }

        script.textContent = JSON.stringify(data);
    }

    private removeStructuredData(id: string): void {
        const selector = `script[type="application/ld+json"][data-seo-id="${id}"]`;
        const script = document.querySelector(selector);
        if (script) {
            this.managedElements.delete(script);
            script.remove();
        }
    }

    applyOptions(options: SEOOptions): void {
        // Keep native check here since router calls this directly
        if (this.isNativeApp) return;

        // Title
        if (options.title !== undefined) {
            this.setTitle(unref(options.title));
        }

        // Basic meta tags
        if (options.description !== undefined) {
            this.setMetaTag('description', unref(options.description));
        }

        if (options.keywords !== undefined) {
            const keywordsValue = unref(options.keywords);
            const keywords = Array.isArray(keywordsValue) ? keywordsValue.join(', ') : keywordsValue;
            this.setMetaTag('keywords', keywords);
        }

        if (options.robots !== undefined) {
            this.setMetaTag('robots', options.robots);
        }

        // Open Graph tags
        if (options.ogTitle !== undefined) {
            this.setMetaTag('og:title', unref(options.ogTitle), 'property');
        }

        if (options.ogDescription !== undefined) {
            this.setMetaTag('og:description', unref(options.ogDescription), 'property');
        }

        if (options.ogImage !== undefined) {
            this.setMetaTag('og:image', unref(options.ogImage), 'property');
        }

        if (options.ogType !== undefined) {
            this.setMetaTag('og:type', options.ogType, 'property');
        }

        if (options.ogUrl !== undefined) {
            this.setMetaTag('og:url', options.ogUrl, 'property');
        }

        // Twitter Card tags
        if (options.twitterCard !== undefined) {
            this.setMetaTag('twitter:card', options.twitterCard);
        }

        if (options.twitterTitle !== undefined) {
            this.setMetaTag('twitter:title', options.twitterTitle);
        }

        if (options.twitterDescription !== undefined) {
            this.setMetaTag('twitter:description', options.twitterDescription);
        }

        if (options.twitterImage !== undefined) {
            this.setMetaTag('twitter:image', options.twitterImage);
        }

        if (options.twitterSite !== undefined) {
            this.setMetaTag('twitter:site', options.twitterSite);
        }

        if (options.twitterCreator !== undefined) {
            this.setMetaTag('twitter:creator', options.twitterCreator);
        }

        // Canonical URL
        if (options.canonical !== undefined) {
            this.setCanonical(unref(options.canonical));
        }

        // Structured data
        if (options.jsonLd !== undefined) {
            const jsonLdValue = unref(options.jsonLd);
            if (Array.isArray(jsonLdValue)) {
                jsonLdValue.forEach((data, index) => {
                    this.setStructuredData(data, `jsonld-${index}`);
                });
            } else {
                this.setStructuredData(jsonLdValue, 'jsonld-0');
            }
        }

        // Publish time
        if (options.publishTime !== undefined) {
            const publishTime = options.publishTime instanceof Date ? options.publishTime.toISOString() : options.publishTime;
            this.setMetaTag('og:published_time', publishTime, 'property');
        }
    }

    reset(): void {
        // Remove all managed elements
        this.managedElements.forEach((element) => {
            element.remove();
        });
        this.managedElements.clear();

        // Remove any canonical links
        const canonicalLinks = document.querySelectorAll('link[rel="canonical"]');
        canonicalLinks.forEach((link) => link.remove());

        // Remove any structured data scripts
        const structuredDataScripts = document.querySelectorAll('script[type="application/ld+json"][data-seo-id]');
        structuredDataScripts.forEach((script) => script.remove());

        // Restore defaults
        this.setDefaults();
    }

    cleanup(): void {
        // Remove all managed elements from DOM
        this.managedElements.forEach((element) => {
            element.remove();
        });
        this.managedElements.clear();

        // Also remove any elements with seo-managed attribute
        const seoManagedElements = document.querySelectorAll('[data-seo-managed="true"]');
        seoManagedElements.forEach((element) => element.remove());
    }

    private setEnvironmentRobots(): void {
        // Set robots based on environment (matching old updateMeta behavior)
        const robotsContent = process.env.NODE_ENV === 'production' ? 'index' : 'noindex';
        this.setMetaTag('robots', robotsContent);
    }

    private setCurrentPageUrl(): void {
        // Set current page URL in og:url meta tag
        this.setMetaTag('og:url', window.location.href, 'property');
    }

    private processImageUrl(imageUrl: string): string {
        // Process image URL to make it absolute (matching old updateMeta behavior)
        if (imageUrl.includes('http')) {
            return imageUrl;
        }
        return window.location.origin + '/pictures/' + imageUrl;
    }

    updateCurrentPageUrl(): void {
        // Keep native check here since router calls this directly
        if (this.isNativeApp) return;

        // Public method to update URL when route changes
        this.setCurrentPageUrl();
    }
}
